import type { AxiosError } from 'axios';
import type { ApiError } from '@/types';

/**
 * E-13 (6-oct) — EL texto del error de red, en UN solo lugar. Sin `response` no hubo ida y vuelta con el servidor (offline, DNS, servidor caído,
 * timeout): el texto de Axios («Network Error», «timeout of 15000ms exceeded») es inglés crudo y NUNCA se le enseña a la persona.
 * Lo heredan `extractApiError` (~23 pantallas), `avisoDeError` y la pantalla de Pagos; nadie lo traduce por su cuenta.
 */
export const MENSAJE_SIN_CONEXION = 'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';

/**
 * BUG-4068 — el servidor SÍ contestó (hay `response`) pero sin texto (un 500 sin cuerpo): `err.message` de Axios sería «Request failed with status code 500»,
 * inglés crudo. Un 5xx dice que el servidor tuvo un problema; cualquier otro rebote sin texto, el mismo respaldo que ya usa el interceptor global.
 */
export const MENSAJE_FALLA_DEL_SERVIDOR = 'El servidor tuvo un problema y no pudo contestar. Intenta de nuevo en un momento; si sigue igual, avisa.';
const MENSAJE_SIN_TEXTO = 'No se pudo completar la acción.';

function esErrorDeRed(err: unknown): boolean {
  const e = err as AxiosError | null | undefined;
  return !!e && e.isAxiosError === true && !e.response;
}

function esRespuestaSinTexto(err: unknown): boolean {
  const e = err as AxiosError | null | undefined;
  return !!e && e.isAxiosError === true && !!e.response;
}

/**
 * El texto de un error de API para la pantalla: el motivo del servidor; sin red, que no hubo conexión; un 5xx sin texto, que el servidor tuvo un problema; y un rebote sin texto, el
 * `respaldo` de la ACCIÓN que la pantalla pasa («Error al validar») — o el genérico si no pasa ninguno. BUG-4074: antes cada pantalla caía a `err.message` de Axios («Network Error»,
 * «Request failed with status code 500») y su texto amable nunca se alcanzaba. Sin `respaldo` se comporta EXACTAMENTE igual que siempre.
 */
export function extractApiError(err: unknown, respaldo?: string): string {
  const axiosErr = err as AxiosError<ApiError>;
  return (
    mensajeDelServidor(err) ||   // BUG-4074: un `message` con sólo espacios NO es un motivo (antes se pintaba en blanco; `mensajeDelServidor` lo trata como «no hubo»)
    (esErrorDeRed(err) ? MENSAJE_SIN_CONEXION : null) ||
    (esRespuestaSinTexto(err) ? ((axiosErr.response?.status ?? 0) >= 500 ? MENSAJE_FALLA_DEL_SERVIDOR : (respaldo ?? MENSAJE_SIN_TEXTO)) : null) ||
    (err instanceof Error ? err.message : (respaldo ?? 'Error inesperado'))
  );
}

// ─── I-234 (T14·14) — el motivo que el servidor dio, o `null` ─────────────────────────────
// Dueño ÚNICO de «¿el servidor rebotó con un texto legible?». Devuelve `null` (no un genérico)
// para que cada pantalla ponga SU respaldo: así el texto amable de cada acción se conserva.
// Sustituye las 3 copias locales que vivían en IngresosList, ConciliacionSplitView y ConciliacionTab.
export function mensajeDelServidor(err: unknown): string | null {
  const m = (err as { response?: { data?: { message?: unknown } } } | null | undefined)?.response?.data?.message;
  return typeof m === 'string' && m.trim() ? m : null;
}

// ─── I-258 — el CÓDIGO, el CAMPO y los DETALLES con que el servidor rebotó (mismo dueño) ──────────
// Para las pantallas que deciden qué ofrecer según el rebote (declarar un pago vivo, declarar sin contrato):
// nadie lee `response.data` a mano (BUG-3610).
// BUG-4091 — el STATUS con que el servidor rebotó (403, 500…), o `null` si ni le pegó. Mismo dueño: la pantalla que distingue «no tienes permiso» de
// «falló» lo pregunta aquí, no con `response?.status` escrito a mano (hay copias en ConciliarComprobante y SeccionResumen).
export function statusDelServidor(err: unknown): number | null {
  const st = (err as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  return typeof st === 'number' ? st : null;
}
export function codigoDelServidor(err: unknown): string | null {
  const c = (err as { response?: { data?: { code?: unknown } } } | null | undefined)?.response?.data?.code;
  return typeof c === 'string' ? c : null;
}
export function campoDelServidor(err: unknown): string | null {
  const f = (err as { response?: { data?: { field?: unknown } } } | null | undefined)?.response?.data?.field;
  return typeof f === 'string' ? f : null;
}
export function detallesDelServidor<T>(err: unknown): T | null {
  const d = (err as { response?: { data?: { details?: unknown } } } | null | undefined)?.response?.data?.details;
  return d && typeof d === 'object' ? (d as T) : null;
}

// ─── BUG-3610 — el mismo motivo, cuando el servidor lo manda dentro de un Blob ─────────────────
// Una descarga (`responseType: 'blob'`) que rebota trae el cuerpo JSON como Blob: hay que leerlo para
// sacar el `message`. Misma pregunta que `mensajeDelServidor`, otra forma del cuerpo ⇒ vive aquí, en el dueño.
export function errorTraeCuerpoBlob(err: unknown): boolean {
  return (err as { response?: { data?: unknown } } | null | undefined)?.response?.data instanceof Blob;
}

export async function mensajeDelServidorDeBlob(err: unknown): Promise<string | null> {
  const cuerpo = (err as { response?: { data?: unknown } } | null | undefined)?.response?.data;
  if (!(cuerpo instanceof Blob)) return mensajeDelServidor(err);
  try {
    const m = (JSON.parse(await cuerpo.text()) as { message?: unknown }).message;
    return typeof m === 'string' && m.trim() ? m : null;
  } catch {
    return null;
  }
}

// ─── I-203 §4 (BUG-2134/BUG-2137) — «nada queda mudo» ────────────────────────────────
//
// 🔴 Se AMPLÍA `extractApiError`, no se sustituye ni se duplica (Global Constraint del
// plan §4: `mensajeDeError.ts` está PROHIBIDO). `extractApiError` sigue funcionando
// IGUAL — tiene ~23 consumidores en M01·M02·M05·M06·M07·M09·M10·ventas·casa (medido con
// `grep -rn "extractApiError" frontend/src`) y ninguno cambia de mensaje con esto.
//
// `avisoDeError()` cubre las respuestas que TODAVÍA no tenían texto amable: sin red
// (el error nunca llega a tener `response`, es puro axios/network) y los códigos de
// negocio que el diseño pide explicar (422 CUENTA_DESTINO_REQUERIDA, 502 POLIZA_FALLIDA).
// El 423 PERIODO_CERRADO NO se resuelve aquí — su detector único sigue siendo
// `periodoDelRebote()` (`components/casa/RebotePeriodoCerrado.tsx`), que además necesita
// la lista de cierres ya cargada; mezclar los dos detectores sería el segundo `if
// (status === 423)` que el plan prohíbe.
export function avisoDeError(err: unknown): string {
  const axiosErr = err as AxiosError<ApiError>;

  // Sin `response`: la petición ni le pegó al servidor (offline, DNS, timeout). Un
  // `extractApiError` puro aquí cae a `err.message` ("Network Error"), que es inglés
  // técnico de Axios — no algo que alguien sin jerga pueda leer.
  if (axiosErr && !axiosErr.response) {
    return MENSAJE_SIN_CONEXION;
  }

  const code = axiosErr?.response?.data?.code;
  const status = axiosErr?.response?.status;

  if (code === 'CUENTA_DESTINO_REQUERIDA') {
    return 'Falta decir a qué cuenta de banco entró este cobro. Elígela e intenta de nuevo.';
  }
  if (status === 502 && code === 'POLIZA_FALLIDA') {
    return 'El cobro sí quedó registrado; la póliza contable no se pudo generar. Vuelve a intentar la póliza desde el detalle del cobro.';
  }

  // Cualquier otro caso (incluido un 500 sin `code` reconocido): el mensaje que el
  // servidor SÍ mandó — nunca la consola, nunca en silencio.
  return extractApiError(err);
}
